/**
 * Backend Configuration Fetcher
 * Location: Mythbound.App/src/utils/backendConfig.ts
 * Created: August 2025
 * 
 * This utility fetches public configuration from the backend API instead of 
 * storing sensitive data in frontend environment variables.
 * 
 * SECURITY IMPROVEMENT:
 * - Client IDs fetched from backend at runtime
 * - No sensitive data stored in frontend bundle
 * - Backend controls what configuration is exposed
 * - Production-ready credential management
 */

import { getApiBaseUrl } from './environment';

interface BackendConfig {
  discordClientId: string | null;
  googleClientId: string | null;
  discordUserId: string | null;
  apiBaseUrl: string;
  environment: string;
}

let configCache: BackendConfig | null = null;
let configPromise: Promise<BackendConfig> | null = null;

/**
 * Fetch public configuration from backend API
 * 
 * RUNTIME CONFIGURATION FETCHING:
 * - Fetches OAuth client IDs from backend securely
 * - Caches configuration to avoid repeated API calls
 * - Provides fallback values for development
 * - Handles network errors gracefully
 * 
 * @returns Promise resolving to backend configuration
 */
export async function getBackendConfig(): Promise<BackendConfig> {
  // Return cached config if available
  if (configCache) {
    return configCache;
  }

  // Return existing promise if fetch is in progress
  if (configPromise) {
    return configPromise;
  }

  // Start new fetch
  configPromise = fetchConfigFromBackend();
  
  try {
    configCache = await configPromise;
    return configCache;
  } catch (error) {
    console.error('[BackendConfig] Failed to fetch backend config:', error);
    
    // Reset promise so it can be retried
    configPromise = null;
    
    // Return fallback configuration for development
    const fallbackConfig: BackendConfig = {
      discordClientId: process.env.REACT_APP_DISCORD_CLIENT_ID || null,
      googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || null,
      discordUserId: null,
      apiBaseUrl: getApiBaseUrl(),
      environment: 'development'
    };
    
    console.warn('[BackendConfig] Using fallback configuration:', fallbackConfig);
    return fallbackConfig;
  }
}

/**
 * Internal function to fetch configuration from backend
 */
async function fetchConfigFromBackend(): Promise<BackendConfig> {
  const apiBaseUrl = getApiBaseUrl();
  const configUrl = `${apiBaseUrl}/config/public`;
  
  console.log('[BackendConfig] Fetching configuration from:', configUrl);
  
  const response = await fetch(configUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session-based auth
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const config = await response.json();
  console.log('[BackendConfig] Successfully fetched backend configuration');
  
  return {
    discordClientId: config.discordClientId,
    googleClientId: config.googleClientId,
    discordUserId: config.discordUserId,
    apiBaseUrl: config.apiBaseUrl || apiBaseUrl,
    environment: config.environment || 'development'
  };
}

/**
 * Get Discord client ID from backend configuration
 * 
 * @returns Promise resolving to Discord client ID or null
 */
export async function getDiscordClientId(): Promise<string | null> {
  const config = await getBackendConfig();
  return config.discordClientId;
}

/**
 * Get Google client ID from backend configuration
 * 
 * @returns Promise resolving to Google client ID or null
 */
export async function getGoogleClientId(): Promise<string | null> {
  const config = await getBackendConfig();
  return config.googleClientId;
}

/**
 * Get Discord user ID from backend configuration
 * 
 * @returns Promise resolving to Discord user ID or null
 */
export async function getDiscordUserId(): Promise<string | null> {
  const config = await getBackendConfig();
  return config.discordUserId;
}

/**
 * Clear cached configuration (useful for testing or when config changes)
 */
export function clearConfigCache(): void {
  configCache = null;
  configPromise = null;
  console.log('[BackendConfig] Configuration cache cleared');
}

/**
 * Check if backend configuration has been loaded
 */
export function isConfigLoaded(): boolean {
  return configCache !== null;
}

/**
 * Get cached configuration without making API call
 * Returns null if configuration hasn't been loaded yet
 */
export function getCachedConfig(): BackendConfig | null {
  return configCache;
}